/**
 * @fileoverview Hook global que espelha o número de DMs/menções não lidas do
 * Chat Interno no ÍCONE do app (PWA) via Badging API — igual o WhatsApp mostra
 * o número no ícone.
 *
 * Deve ser usado no LoggedInLayout (sempre montado p/ usuário autenticado), pra
 * o badge refletir mesmo quando o user não está em /internal-chat.
 *
 * Escopo: só DMs/privadas e menções (fonte: campo `dmMentions` de
 * /internal-chat/unread-counts). Canais comuns não entram no badge do ícone.
 *
 * Limitação: setAppBadge só funciona no PWA instalado e enquanto o app está
 * RODANDO (aberto, mesmo minimizado). Com o app totalmente fechado o badge não
 * atualiza — isso exigiria service worker + Web Push (fora do escopo).
 */

import { useContext, useEffect, useRef } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import usePermission from "../usePermission";
import { isAttendanceOnly } from "../../utils/accessScope";

const supportsBadge = () =>
  typeof navigator !== "undefined" && "setAppBadge" in navigator;

const useInternalChatAppBadge = () => {
  const { user } = useContext(AuthContext);
  // Quem usa o chat interno: hoje, quem não é "Apenas Atendimento"; com a regra
  // nova, internal_chat.use. Espera o acesso chegar, para não pedir antes.
  const { can, loaded } = usePermission();
  const usesInternalChat = loaded && can("internal_chat.use", !isAttendanceOnly(user));
  const socketManager = useContext(SocketContext);
  const lastCountRef = useRef(-1);

  useEffect(() => {
    if (!supportsBadge()) return undefined;
    if (!user?.id || !user?.companyId) return undefined;
    if (!usesInternalChat) return undefined; // sem o chat interno

    let cancelled = false;
    let debounceTimer;

    const applyBadge = (count) => {
      if (cancelled || count === lastCountRef.current) return;
      lastCountRef.current = count;
      try {
        if (count > 0) navigator.setAppBadge(count);
        else navigator.clearAppBadge();
      } catch {
        // Badging pode falhar em alguns ambientes — ignora silenciosamente.
      }
    };

    const refresh = async () => {
      try {
        const { data } = await api.get("/internal-chat/unread-counts");
        if (!cancelled) applyBadge(Number(data?.dmMentions) || 0);
      } catch {
        // ignore
      }
    };

    const scheduleRefresh = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(refresh, 500);
    };

    // Estado inicial ao montar/logar.
    refresh();

    // Eventos do próprio usuário (nova DM, menção, leitura de canal) chegam em
    // `user-${id}` — qualquer um deles pode mudar a contagem, então re-busca.
    const socket = socketManager.getSocket(user.companyId);
    const userEvent = `user-${user.id}`;
    const handleUserEvent = () => scheduleRefresh();
    socket.on(userEvent, handleUserEvent);

    // Ao voltar o foco pra aba, re-sincroniza (ex.: leu em outro dispositivo).
    const handleVisibility = () => {
      if (document.visibilityState === "visible") scheduleRefresh();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
      socket.off(userEvent, handleUserEvent);
      document.removeEventListener("visibilitychange", handleVisibility);
      try {
        navigator.clearAppBadge();
      } catch {
        // ignore
      }
      // O ícone ficou sem número: a próxima contagem o põe de novo, mesmo igual.
      lastCountRef.current = -1;
    };
  }, [socketManager, user?.id, user?.companyId, usesInternalChat]);
};

export default useInternalChatAppBadge;
